import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header / AppBar */}
      <header className="bg-indigo-600 text-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
            Privacy Policy
          </h1>
        </div>
      </header>

      {/* Main scrollable content */}
      <main className="max-w-4xl mx-auto px-5 sm:px-6 py-8 md:py-10">
        <div className="prose prose-gray max-w-none">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Privacy Policy</h2>

          <p className="text-indigo-600 font-semibold text-lg mb-1">
            Powered by OneHexa Tech
          </p>

          <p className="text-gray-500 text-sm mb-10">
            Last updated: February 12, 2026
          </p>

          {/* 1. Introduction */}
          <Section
            number="1"
            title="Introduction"
            content={
              'Welcome to "Data Vault" (the "App"), developed and powered by OneHexa Tech. ' +
              'We are committed to protecting your personal information and your right to privacy. ' +
              'This Privacy Policy explains what information we collect, how we use it, where it is stored, ' +
              'and your rights regarding your data when you use our secure digital vault services.'
            }
          />

          {/* 2. Permissions We Request */}
          <SectionWithIcon
            number="2"
            title="Permissions We Request"
            icon="🔒"
            content={
              'Our App requests the following device permissions to provide its core features. ' +
              'Each permission is strictly limited to its intended purpose:\n\n' +
              '• Internet (INTERNET):\n' +
              '  Used to connect to our secure servers for account authentication, syncing your notes, ' +
              'checklists, documents, and folders across devices in real-time.\n\n' +
              '• Camera (CAMERA):\n' +
              '  Used exclusively within the "Document Save" feature. When you choose to upload a document, ' +
              'you can capture a photo directly from your camera. We do NOT access the camera in the background.\n\n' +
              '• Media / Gallery Access (READ_MEDIA_IMAGES):\n' +
              '  Used to let you pick existing images or documents from your device gallery ' +
              'for uploading to the Document Save feature. We only access the files you explicitly select.\n\n' +
              '• Biometric / Device Lock (USE_BIOMETRIC):\n' +
              '  Used for the optional "App Lock" and "Quick Login" feature. Biometric data ' +
              '(Fingerprint / Face ID) is NEVER sent to our servers — all verification happens ' +
              'entirely on your device hardware.'
            }
          />

          {/* 3. Data We Collect */}
          <SectionWithIcon
            number="3"
            title="Data We Collect"
            icon="📊"
            content={
              'We collect only the minimum information required to deliver our services:\n\n' +
              '• Account Information:\n' +
              '  Name, Email Address, and Mobile Number — collected during registration. ' +
              'Used for account creation, login authentication, OTP verification, and communication.\n\n' +
              '• Password:\n' +
              '  Your account password is hashed using bcrypt before being stored. We NEVER store or see ' +
              'your plain-text password.\n\n' +
              '• Google Profile Data:\n' +
              '  If you sign in via "Continue with Google", we receive your Name, Email, and Profile Picture ' +
              'from Google. This is used solely for account creation and identification.\n\n' +
              '• Device ID:\n' +
              '  A unique device identifier (UUID) is generated and stored locally on your device. ' +
              'It is sent to the server during login to link your session to your device for security purposes.\n\n' +
              '• User-Created Content:\n' +
              '  - Notes: Title and content you create (stored with AES-256 encryption on our server)\n' +
              '  - Checklists: Shopping lists and task items you create\n' +
              '  - Documents: Images/files you explicitly upload via camera or gallery\n' +
              '  - Folders: Folder names and their password-protected structure\n\n' +
              '• Authentication Token:\n' +
              '  A JWT (JSON Web Token) is stored locally on your device after login to keep you signed in securely.'
            }
          />

          {/* 4. How & Where We Use Your Data */}
          <SectionWithIcon
            number="4"
            title="How & Where We Use Your Data"
            icon="💾"
            content={
              'Every piece of data we collect has a specific purpose:\n\n' +
              '• Account & Authentication:\n' +
              '  Your name, email, mobile, and password are used for registration, login, ' +
              'OTP verification, and profile display. Device ID is used to enforce device-level ' +
              'security and prevent unauthorized access.\n\n' +
              '• Notes Feature:\n' +
              '  Your notes are encrypted with AES-256-CBC encryption before being stored on our server. ' +
              'They are synced across your logged-in devices and decrypted only on your device.\n\n' +
              '• Checklist / Shopping List:\n' +
              '  Your checklist titles and items are stored on our server to enable ' +
              'creation, editing, progress tracking, and cross-device sync.\n\n' +
              '• Document Save:\n' +
              '  Documents you upload (via camera or gallery) are sent to our server and stored securely. ' +
              'You can view, download, and delete your uploaded documents at any time.\n\n' +
              '• Folders & Password Lock:\n' +
              '  Folder structure and names are stored on the server. Folder passwords are ' +
              'verified server-side to restrict access to sensitive content.\n\n' +
              '• Data Export:\n' +
              '  You can export your profile and notes data as a PDF document from within the App. ' +
              'This is generated locally on your device.\n\n' +
              '• Real-Time Communication:\n' +
              '  We use Socket.IO to enable real-time updates and event notifications within the App.'
            }
          />

          {/* 5. Third-Party Services & Advertising */}
          <SectionWithIcon
            number="5"
            title="Third-Party Services & Advertising"
            icon="📢"
            content={
              'Our App integrates the following third-party services:\n\n' +
              '• Yodo1 MAS SDK (Advertising):\n' +
              '  We use the Yodo1 MAS advertising SDK, which includes Google AdMob, to display ' +
              'banner and interstitial advertisements. This SDK may collect device identifiers, ' +
              'advertising IDs, and general usage data in accordance with its own privacy policies ' +
              'to serve relevant ads. We do NOT control the data collected by the ad SDK.\n\n' +
              '• Google Sign-In:\n' +
              '  We use Google Sign-In for convenient account creation and login. When you sign in via Google, ' +
              'we receive your basic profile (Name, Email, Profile Picture) and a secure ID token. ' +
              'We do NOT access your Google Drive, contacts, or any other Google data.\n\n' +
              '• Google AdMob:\n' +
              '  Google AdMob is used as part of the Yodo1 MAS integration to serve advertisements. ' +
              "AdMob may collect and process user data as described in Google's Privacy Policy."
            }
          />

          {/* 6. Device & Login Security */}
          <SectionWithIcon
            number="6"
            title="Device & Login Security"
            icon="📱"
            content={
              'Your account security is our top priority:\n\n' +
              '• Device Linking:\n' +
              '  Each login session is linked to a unique Device ID generated on your device. ' +
              'This ensures only your authorized devices can access your account.\n\n' +
              '• Login Alerts:\n' +
              '  If a login attempt is detected from a new or unknown device, we may notify you ' +
              'or require additional verification via Socket.IO real-time alerts.\n\n' +
              '• Session Management:\n' +
              '  Secure JWT-based sessions ensure only authenticated users access their data. ' +
              'Tokens are stored locally and sent with every API request for verification.'
            }
          />

          {/* 7. Biometric Data (Local Only) */}
          <SectionWithIcon
            number="7"
            title="Biometric Data (Local Only)"
            icon="👆"
            content={
              'We use your device\'s local biometric capabilities (Fingerprint or Face ID) solely for ' +
              'the "App Lock" and "Quick Login" features.\n\n' +
              '• We do NOT collect, store, or transmit your biometric data to our servers.\n' +
              '• All biometric verification happens entirely on your device hardware.\n' +
              '• You can enable or disable biometric lock from your Profile settings at any time.'
            }
          />

          {/* 8. Data Security */}
          <SectionWithIcon
            number="8"
            title="Data Security"
            icon="🛡️"
            content={
              'We implement industry-standard security measures including:\n\n' +
              '• Encryption at Rest:\n' +
              '  Your notes content is encrypted using AES-256-CBC encryption with a secure key before ' +
              'being stored on our servers.\n\n' +
              '• Password Hashing:\n' +
              '  Your passwords are hashed using bcrypt before storage. We never store plain-text passwords.\n\n' +
              '• Encryption in Transit:\n' +
              '  All data communication between your device and our servers uses HTTP over secure connections.\n\n' +
              '• Secure Authentication:\n' +
              '  We use JWT (JSON Web Token) for secure, stateless session management.\n\n' +
              '• Access Control:\n' +
              '  Strict backend validation ensures you can only access your own data. ' +
              'Each API request requires a valid authentication token.\n\n' +
              'While we prioritize security, no method of transmission over the internet is 100% secure, ' +
              'but we strive to protect your data to the fullest extent.'
            }
          />

          {/* 9. Local Storage on Your Device */}
          <SectionWithIcon
            number="9"
            title="Local Storage on Your Device"
            icon="📲"
            content={
              'We store the following data locally on your device using SharedPreferences:\n\n' +
              '• Authentication Token (JWT) — to keep you signed in\n' +
              '• Device ID (UUID) — for device-level security\n' +
              '• User preferences — biometric lock setting, onboarding status\n' +
              '• Basic profile info — username, email (for display in UI)\n\n' +
              'This local data is private to the App and is not shared with other applications.'
            }
          />

          {/* 10. Data Retention & Deletion */}
          <Section
            number="10"
            title="Data Retention & Deletion"
            content={
              'We retain your personal information only for as long as is necessary to provide our services. ' +
              'You can request the deletion of your account and data at any time via the App settings. ' +
              'Upon deletion, your account and all associated data — including notes, checklists, documents, ' +
              'and folders — will be permanently removed from our active servers.'
            }
          />

          {/* 11. Children's Privacy */}
          <Section
            number="11"
            title="Children's Privacy"
            content={
              'Our Service is not directed to anyone under the age of 13. We do not knowingly collect ' +
              'personally identifiable information from children under 13. If you are a parent or guardian ' +
              'and believe your child has provided us with personal data, please contact us.'
            }
          />

          {/* 12. Changes to This Policy */}
          <Section
            number="12"
            title="Changes to This Policy"
            content={
              'We may update this Privacy Policy from time to time. We will notify you of any significant changes ' +
              'by updating the "Last updated" date at the top of this page. Continued use of the App after ' +
              'changes constitutes acceptance of the revised policy.'
            }
          />

          {/* 13. Contact Us */}
          <Section
            number="13"
            title="Contact Us"
            content={
              <>
                If you have any questions about this Privacy Policy or how your data is handled,
                please contact OneHexa Tech support:
                <br /><br />
                <a
                  href="mailto:onehexatech@gmail.com"
                  className="text-indigo-600 hover:underline font-medium"
                >
                  Email: onehexatech@gmail.com
                </a>
              </>
            }
          />

          {/* Footer branding */}
          <div className="mt-16 pt-10 border-t border-gray-200 text-center text-gray-600">
            <p className="text-sm">Powered by</p>
            <p className="mt-1 text-xl font-bold text-indigo-600 tracking-wide">
              OneHexa Tech
            </p>
            <div className="h-6"></div>
          </div>
        </div>
      </main>
    </div>
  );
};

/* ── Simple text section (for shorter/plain sections) ── */
function Section({ number, title, content }) {
  return (
    <div className="mb-11 last:mb-6">
      <h3 className="text-xl font-semibold text-indigo-600 mb-3">
        {number}. {title}
      </h3>
      <div className="text-[15px] leading-relaxed text-gray-800 whitespace-pre-line">
        {content}
      </div>
    </div>
  );
}

/* ── Card section with icon (for key detailed sections) ── */
function SectionWithIcon({ number, title, icon, content }) {
  return (
    <div className="mb-11 last:mb-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-50 text-lg">
          {icon}
        </span>
        <h3 className="text-xl font-semibold text-indigo-600">
          {number}. {title}
        </h3>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <div className="text-[14px] leading-relaxed text-gray-700 whitespace-pre-line">
          {content}
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
